<template>
  <div class="card p-3">
    <h3>Access Level</h3>
    <div class="row mb-2 align-items-center">
      <label class="col-md-3 col-12 d-flex align-items-center">
        <input class="me-2" type="radio" value="full" v-model="accessSelection" /> Full Access
      </label>
      <label class="col-md-3 col-12 d-flex align-items-center">
        <input class="me-2" type="radio" value="view" v-model="accessSelection" /> View Only
      </label>
      <label class="col-md-3 col-12 d-flex align-items-center">
        <input class="me-2" type="radio" value="none" v-model="accessSelection" /> Notifications Only (No Access)
      </label>
      <label class="col-md-3 col-12 d-flex align-items-center">
        <input class="me-2" type="radio" value="custom" v-model="accessSelection" /> Custom
      </label>
    </div>
    <table class="permissions-table">
      <thead>
        <tr>
          <th>Page</th>
          <th>View</th>
          <th>Edit</th>
          <th>Export</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="page in filteredModuleAccess" :key="page.value">
          <td>
            <span v-html="page.label"></span>
            <i 
              class="fas fa-question-circle ms-1 text-muted" 
              data-bs-toggle="tooltip" 
              :title="getPageHelpText(page.value)"
              style="cursor: help;"
            ></i>
          </td>
          <td>
            <template v-if="isPermissionDisabled(page.value, 'view')">
              <span
                class="permission-disabled d-inline-block"
                data-bs-toggle="tooltip"
                title="View functionality is not available for this module"
                tabindex="0"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mb-0"
                  :checked="false"
                  disabled
                  :id="'access-view-' + page.value" aria-label="Checkbox">
              </span>
            </template>
            <template v-else-if="cannotGrantView(page.value)">
              <span
                class="permission-disabled d-inline-block"
                data-bs-toggle="tooltip"
                :title="getTooltip(page.value, 'view')"
                tabindex="0"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mb-0"
                  :checked="permissions[page.value].view"
                  disabled
                  :id="'access-view-' + page.value" aria-label="Checkbox">
              </span>
            </template>
            <input
              v-else
              type="checkbox"
              class="form-check-input me-2 mb-0"
              :checked="permissions[page.value].view"
              @change="handlePermissionChange(page.value, 'view', $event)"
              :id="'access-view-' + page.value" aria-label="Checkbox">
          </td>
          <td>
            <template v-if="isPermissionDisabled(page.value, 'edit')">
              <span
                class="permission-disabled d-inline-block"
                data-bs-toggle="tooltip"
                title="Edit functionality is not available for this module"
                tabindex="0"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mb-0"
                  :checked="false"
                  disabled
                  :id="'access-edit-' + page.value" aria-label="Checkbox">
              </span>
            </template>
            <template v-else-if="cannotGrantView(page.value) || cannotGrantEdit(page.value)">
              <span
                class="permission-disabled d-inline-block"
                data-bs-toggle="tooltip"
                :title="getTooltip(page.value, 'edit')"
                tabindex="0"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mb-0"
                  :checked="permissions[page.value].edit"
                  disabled
                  :id="'access-edit-' + page.value" aria-label="Checkbox">
              </span>
            </template>
            <input
              v-else
              type="checkbox"
              class="form-check-input me-2 mb-0"
              :checked="permissions[page.value].edit"
              @change="handlePermissionChange(page.value, 'edit', $event)"
              :id="'access-edit-' + page.value" aria-label="Checkbox">
          </td>
          <td>
            <template v-if="isPermissionDisabled(page.value, 'export')">
              <span
                class="permission-disabled d-inline-block"
                data-bs-toggle="tooltip"
                title="Export functionality is not available for this module"
                tabindex="0"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mb-0"
                  :id="'access-export-' + page.value"
                  disabled
                  :checked="false" aria-label="Checkbox">
              </span>
            </template>
            <template v-else-if="cannotGrantExport(page.value)">
              <span
                class="permission-disabled d-inline-block"
                data-bs-toggle="tooltip"
                :title="getTooltip(page.value, 'export')"
                tabindex="0"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mb-0"
                  :checked="permissions[page.value].export"
                  disabled
                  :id="'access-export-' + page.value" aria-label="Checkbox">
              </span>
            </template>
            <input
              v-else
              type="checkbox"
              class="form-check-input me-2 mb-0"
              :checked="permissions[page.value].export"
              @change="handlePermissionChange(page.value, 'export', $event)"
              :id="'access-export-' + page.value" aria-label="Checkbox">
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { Tooltip } from 'bootstrap';
import statsigService from '../../services/statsig';

export default {
  props: {
    user_logged_in: {
      type: Object,
      default: null
    },
    initialPermissions: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      permissions: {},
      accessSelection: 'custom',

      // Statsig feature gate tracking
      statsigReady: false,
      hasCheckedDirectMailGate: false,
      directMailGateResult: null,

      module_access: [
            { value: 'benchmarks', label: 'Benchmarks' },
            { value: 'scorecard', label: 'Scorecard' },
            { value: 'insights', label: 'Insights & Reports' },
            { value: 'pathways', label: 'Pathways' },
            { value: 'playbooks', label: 'Playbooks' },
            { value: 'direct_mail', label: 'Direct Mail' },
            { value: 'connections', label: 'Connections & Files' },
            { value: 'settings', label: 'Settings & Team Members' },
            { value: 'billing', label: 'Billing' },
        ],
    };
  },
  async mounted() {
    // Initialize Statsig and check feature gates
    try {
      await statsigService.initialize(this.user_logged_in || {});

      this.directMailGateResult = statsigService.checkGate('direct_mail_access');

      this.hasCheckedDirectMailGate = true;

    } catch (err) {
      console.error('Statsig failed to init, defaulting gates on:', err);
      this.directMailGateResult = true;
      this.hasCheckedDirectMailGate = true;
    } finally {
      this.statsigReady = true;
    }
  },
  beforeUnmount() {
    // Dispose of all tooltips on component unmount
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(el => {
      if (el._tooltipInstance) {
        el._tooltipInstance.dispose();
      }
    });
  },
  computed: {
    // Filter module_access based on Statsig feature gates
    filteredModuleAccess() {
      if (!this.statsigReady) {
        return this.module_access;
      }

      return this.module_access.filter(module => {
        // Hide direct_mail if feature gate is disabled
        if (module.value === 'direct_mail' && !this.checkDirectMailGate()) {
          return false;
        }

        return true;
      });
    },
    access() {
      let allView = true;
      let allEdit = true;
      let allExport = true;
      let allViewOnly = true;
      let allNone = true;
      for (const page of this.filteredModuleAccess) {
        const perms = this.permissions[page.value] || {};
        
        // View - only check if not disabled
        if (!this.isPermissionDisabled(page.value, 'view')) {
          if (!perms.view) allView = false;
          if (perms.view) allNone = false;
        }
        
        // Edit - only check if not disabled
        if (!this.isPermissionDisabled(page.value, 'edit')) {
          if (!perms.edit) allEdit = false;
          if (perms.edit) allViewOnly = false;
        }
        
        // Export - only check if not disabled
        if (!this.isPermissionDisabled(page.value, 'export')) {
          if (!perms.export) allExport = false;
          if (perms.export) allViewOnly = false;
          if (perms.export) allNone = false;
        }
        
        // If edit is enabled and checked, it affects viewOnly and none
        if (!this.isPermissionDisabled(page.value, 'edit') && perms.edit) {
          allViewOnly = false;
          allNone = false;
        }
      }
      
      if (allView && allEdit && allExport) return 'full';
      if (allView && allViewOnly && !allEdit && !allExport) return 'view';
      if (allNone) return 'none';
      return 'custom';
    },
    // Check if logged-in user has access to a specific module and permission
    hasLoggedInUserAccess() {
      return (module, permission) => {
        if (!this.user_logged_in || !this.user_logged_in.access) {
          return false;
        }
        return this.user_logged_in.access[module] && this.user_logged_in.access[module][permission];
      };
    },
    // Helpers for disabling checkboxes and tooltips
    cannotGrantView() {
      return (module) => !this.hasLoggedInUserAccess(module, 'view');
    },
    cannotGrantEdit() {
      return (module) => !this.hasLoggedInUserAccess(module, 'edit');
    },
    cannotGrantExport() {
      return (module) => !this.hasLoggedInUserAccess(module, 'export');
    },
    getTooltip() {
      return (module, perm) => {
        if (!this.hasLoggedInUserAccess(module, 'view')) {
          return "You don't have access to this module so you cannot change access for others on this one.";
        }
        if (perm === 'edit' && !this.hasLoggedInUserAccess(module, 'edit')) {
          return "You don't have edit access to this module so you cannot grant edit or export for others.";
        }
        if (perm === 'export' && !this.hasLoggedInUserAccess(module, 'export')) {
          return "You don't have export access to this module so you cannot grant export for others.";
        }
        return '';
      };
    },
  },
  beforeMount() {
    // If initialPermissions is null, default to full access
    if (this.initialPermissions === null) {
      this.permissions = {};
      this.module_access.forEach(page => {
        this.permissions[page.value] = {
          view: !this.isPermissionDisabled(page.value, 'view'),
          edit: !this.isPermissionDisabled(page.value, 'edit'),
          export: !this.isPermissionDisabled(page.value, 'export')
        };
      });
    } else {
      this.permissions = { ...this.initialPermissions };
    }
    this.accessSelection = this.access;
  },
  updated() {
    // Initialize tooltips on every update
    this.$nextTick(() => {
      this.initTooltips();
    });
  },
  watch: {
    // When the computed access changes (from manual checkbox changes), update the radio group
    permissions: {
      handler() {
        // Enforce hierarchy for each page
        for (const page of this.filteredModuleAccess) {
          const perms = this.permissions[page.value] || {};
          let updated = false;
          
          // 1. If view is unchecked, edit and export are unchecked
          if (!perms.view) {
            if (perms.edit) {
              perms.edit = false;
              updated = true;
            }
            if (perms.export) {
              perms.export = false;
              updated = true;
            }
          }
          
          // 2. If edit is checked, view is checked
          if (perms.edit && !perms.view) {
            perms.view = true;
            updated = true;
          }
          
          // 3. If edit is unchecked, export is unchecked
          if (!perms.edit && perms.export) {
            perms.export = false;
            updated = true;
          }
          
          // 4. If export is checked, edit and view are checked
          if (perms.export) {
            if (!perms.edit) {
              perms.edit = true;
              updated = true;
            }
            if (!perms.view) {
              perms.view = true;
              updated = true;
            }
          }
          
          // Update the permissions object to trigger reactivity
          if (updated) {
            this.permissions[page.value] = { ...perms };
          }
        }
        this.accessSelection = this.access;
      },
      deep: true
    },
    // When the user changes the radio group, update all permissions accordingly
    accessSelection(newVal, oldVal) {
      // Skip emitting events on initial mount
      if (oldVal === undefined) {
        if (newVal === 'full') {
          this.module_access.forEach(page => {
            const perms = this.permissions[page.value] || {};
            perms.view = true;
            perms.edit = true;
            perms.export = true;
            this.permissions[page.value] = perms;
          });
        } else if (newVal === 'view') {
          this.module_access.forEach(page => {
            const perms = this.permissions[page.value] || {};
            perms.view = true;
            perms.edit = false;
            perms.export = false;
            this.permissions[page.value] = perms;
          });
        } else if (newVal === 'none') {
          this.module_access.forEach(page => {
            const perms = this.permissions[page.value] || {};
            perms.view = false;
            perms.edit = false;
            perms.export = false;
            this.permissions[page.value] = perms;
          });
        }
        // Always ensure scorecard export is unchecked
        if (this.permissions.scorecard) {
          this.permissions.scorecard.export = false;
        }
        return;
      }
      
      if (newVal === 'full') {
        this.module_access.forEach(page => {
          const perms = this.permissions[page.value] || {};
          const oldPerms = { ...perms };
          perms.view = true;
          perms.edit = true;
          perms.export = true;
          this.permissions[page.value] = perms;
          
          // Emit events for changed permissions
          ['view', 'edit', 'export'].forEach(func => {
            if (oldPerms[func] !== perms[func]) {
              this.$emit('permission-change', {
                section: page.value,
                functionality: func,
                value: perms[func]
              });
            }
          });
        });
      } else if (newVal === 'view') {
        this.module_access.forEach(page => {
          const perms = this.permissions[page.value] || {};
          const oldPerms = { ...perms };
          perms.view = true;
          perms.edit = false;
          perms.export = false;
          this.permissions[page.value] = perms;
          
          // Emit events for changed permissions
          ['view', 'edit', 'export'].forEach(func => {
            if (oldPerms[func] !== perms[func]) {
              this.$emit('permission-change', {
                section: page.value,
                functionality: func,
                value: perms[func]
              });
            }
          });
        });
      } else if (newVal === 'none') {
        this.module_access.forEach(page => {
          const perms = this.permissions[page.value] || {};
          const oldPerms = { ...perms };
          perms.view = false;
          perms.edit = false;
          perms.export = false;
          this.permissions[page.value] = perms;
          
          // Emit events for changed permissions
          ['view', 'edit', 'export'].forEach(func => {
            if (oldPerms[func] !== perms[func]) {
              this.$emit('permission-change', {
                section: page.value,
                functionality: func,
                value: perms[func]
              });
            }
          });
        });
      } else if (newVal === 'custom') {
        // Do nothing, user will set manually
      }
      // Always ensure scorecard export is unchecked
      if (this.permissions.scorecard) {
        this.permissions.scorecard.export = false;
      }
    },
    initialPermissions: {
      handler(newVal) {
        // If initialPermissions is null, default to full access
        if (newVal === null) {
          this.permissions = {};
          this.module_access.forEach(page => {
            this.permissions[page.value] = {
              view: !this.isPermissionDisabled(page.value, 'view'),
              edit: !this.isPermissionDisabled(page.value, 'edit'),
              export: !this.isPermissionDisabled(page.value, 'export')
            };
          });
        } else {
          this.permissions = { ...newVal };
        }
      },
      deep: true
    }
  },
  methods: {
    // Statsig feature gate checking methods (same as Sidebar)
    checkDirectMailGate() {
      // If we've already checked the gate, return the cached result
      if (this.hasCheckedDirectMailGate) {
        return this.directMailGateResult;
      }

      // Mark that we've checked the gate
      this.hasCheckedDirectMailGate = true;

      try {
        // Check the direct_mail_access feature gate using the service
        const hasAccess = statsigService.checkGate('direct_mail_access');

        // Cache the result
        this.directMailGateResult = hasAccess;

        return hasAccess;
      } catch (error) {
        // If there's an error, fall back to the default logic
        this.directMailGateResult = true;
        return true;
      }
    },

    initTooltips() {
      // Remove any existing tooltips to avoid duplicates
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.forEach(el => {
        try {
          if (el._tooltipInstance) {
            el._tooltipInstance.dispose();
          }
          el._tooltipInstance = new Tooltip(el);
        } catch (e) {
          // Defensive: ignore errors if element is not valid for tooltip
          // Optionally log: console.warn('Tooltip init error:', e);
        }
      });
    },
    handlePermissionChange(page, perm, event) {
      const checked = event.target.checked;
      const perms = { ...this.permissions[page] };
      perms[perm] = checked;
      // Enforce hierarchy
      if (perm === 'view' && !checked) {
        perms.edit = false;
        perms.export = false;
      }
      if (perm === 'edit') {
        if (checked) {
          perms.view = true;
        } else {
          perms.export = false;
        }
      }
      if (perm === 'export') {
        if (checked) {
          perms.edit = true;
          perms.view = true;
        }
      }
      this.permissions[page] = perms;
      this.accessSelection = this.access;
      
      // Emit permission change event
      this.$emit('permission-change', {
        section: page,
        functionality: perm,
        value: checked
      });
    },
    getPermissions() {
      return this.permissions;
    },
    getPageHelpText(pageValue) {
      const helpTexts = {
        'scorecard': 'Get a snapshot view of your organization\'s performance and be able to run "what if" scenarios to understand the impact of different decisions.',
        'insights': 'Access detailed analytics and reporting insights about your data. Export access will allow you to generate PowerPoint presentations.',
        'playbooks': 'View and manage strategic playbooks and campaign templates. Export access will allow you to deploy these plays to your advertising and marketing tools.',
        'direct_mail': 'Generate your audience selections for direct mail campaigns. This will also grant access to Audience creation.',
        'connections': 'Configure and manage third-party integrations and data sources. This include column mapping and file uploads.',
        'settings': 'Access organization settings such as fiscal year and donor definitions and be able to invite new team members or update the permissions of existing individuals.',
        'benchmarks': 'View industry benchmarks and comparative data.',
        'pathways': 'Push data to your advertising and marketing tools. Access to this module will also grant access to the Audience creation module.',
        'billing': 'View and manage your organization\'s billing information. This will also restrict who can approve additional charges such as new Pathways.',
      };
      
      return helpTexts[pageValue] || `Help information for ${pageValue} page.`;
    },
    isPermissionDisabled(page, perm) {
      // Scorecard export is always disabled
      if (page === 'scorecard' && perm === 'export') return true;
      // Pathways export is always disabled
      if (page === 'benchmarks' && perm === 'edit') return true;
      // Settings export is always disabled
      if (page === 'settings' && perm === 'export') return true;
      // Settings export is always disabled
      if (page === 'billing' && perm === 'export') return true;
      return false;
    }
  }
};
</script>